import React, { useState, useEffect } from 'react';
import { Modal, Button, Spin, Alert, Tabs, Typography, Card, Space, Tag, Badge } from 'antd';
import { 
    InfoCircleOutlined, 
    HeartOutlined, 
    FireOutlined, 
    ExperimentOutlined, 
    AppleOutlined,
    RobotOutlined,
    MessageOutlined,
    ThunderboltOutlined,
    QuestionCircleOutlined,
    MailOutlined,
    ShoppingCartOutlined
} from '@ant-design/icons';
import RichTextRenderer from './RichTextRenderer';
import OrderAssistanceModal from './OrderAssistanceModal';
import aiService from '../../services/aiService';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

// Filter out technical content from AI responses
const filterTechnicalContent = (response) => {
    // Remove code blocks
    let filtered = response.replace(/```[\s\S]*?```/g, '');
    
    // Remove code snippets
    filtered = filtered.replace(/`[^`]+`/g, '');
    
    // Remove programming-related content
    const programmingPatterns = [
        /class\s+\w+\s*{[\s\S]*?}/g,
        /def\s+\w+\([^)]*\):[\s\S]*?(?=\n\w|\n\n|$)/g,
        /function\s+\w+\([^)]*\)\s*{[\s\S]*?}/g,
        /import\s+[\w\s,]+/g,
        /from\s+[\w\s]+\s+import/g,
        /console\.log\([^)]*\)/g,
        /print\([^)]*\)/g,
        /# [^\n]*/g,
        /\/\/ [^\n]*/g,
        /\/\*[\s\S]*?\*\//g
    ];
    
    programmingPatterns.forEach(pattern => {
        filtered = filtered.replace(pattern, '');
    });
    
    // Clean up extra whitespace
    filtered = filtered.replace(/\n\s*\n\s*\n/g, '\n\n');
    filtered = filtered.trim();
    
    // If response becomes too short or empty, provide fallback
    if (filtered.length < 50) {
        return "I'm here to help with food-related questions! How can I assist you with information about this dish?";
    }
    
    return filtered;
};

const AIInfoModal = ({ 
    visible, 
    onClose, 
    menuItem, 
    title = "AI Information",
    showCustomChat = true 
}) => {
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [aiData, setAiData] = useState({
        overview: null,
        nutrition: null,
        cooking: null,
        ingredients: null,
        dietary: null
    });
    const [error, setError] = useState(null);
    const [customQuery, setCustomQuery] = useState('');
    const [customResponse, setCustomResponse] = useState(null);
    const [customLoading, setCustomLoading] = useState(false);
    const [orderAssistanceVisible, setOrderAssistanceVisible] = useState(false);

    useEffect(() => {
        if (visible && menuItem) {
            loadAIInfo();
        }
    }, [visible, menuItem]);

    const loadAIInfo = async () => {
        if (!menuItem) return;

        setLoading(true);
        setError(null);

        try {
            // Load overview information first
            const overviewResult = await aiService.getMenuItemInfo(menuItem);
            if (overviewResult.success) {
                setAiData(prev => ({ ...prev, overview: overviewResult.data }));
            }

            // Load other information in parallel
            const [nutritionResult, cookingResult, ingredientsResult] = await Promise.all([
                aiService.getNutritionalInfo(menuItem),
                aiService.getCookingTips(menuItem),
                aiService.getIngredientAnalysis(menuItem)
            ]);

            if (nutritionResult.success) {
                setAiData(prev => ({ ...prev, nutrition: nutritionResult.data }));
            }
            if (cookingResult.success) {
                setAiData(prev => ({ ...prev, cooking: cookingResult.data }));
            }
            if (ingredientsResult.success) {
                setAiData(prev => ({ ...prev, ingredients: ingredientsResult.data }));
            }

        } catch (err) {
            setError('Failed to load AI information. Please try again.');
            console.error('AI info loading error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCustomQuery = async () => {
        if (!customQuery.trim()) return;

        setCustomLoading(true);
        setCustomResponse(null);

        try {
            // Check if query is about coding/programming/technical topics
            const technicalKeywords = [
                'code', 'programming', 'python', 'javascript', 'class', 'function', 'variable', 
                'algorithm', 'database', 'api', 'html', 'css', 'react', 'node', 'php', 'sql',
                'generate', 'create', 'write', 'build', 'develop', 'implement', 'script',
                'program', 'software', 'application', 'framework', 'library', 'module',
                'syntax', 'compile', 'debug', 'deploy', 'repository', 'git', 'github',
                'json', 'xml', 'yaml', 'markdown', 'docker', 'kubernetes', 'aws', 'azure'
            ];
            const isTechnicalQuery = technicalKeywords.some(keyword => 
                customQuery.toLowerCase().includes(keyword.toLowerCase())
            );

            if (isTechnicalQuery) {
                setCustomResponse(`I'm here to help with information about ${menuItem.name}! I can assist you with:

🍽️ **Dish Information** - Ingredients, preparation, cooking methods
🌶️ **Spice Level** - Heat level and flavor profile
🥗 **Dietary Info** - Vegetarian, vegan, gluten-free, allergens
⏰ **Preparation Time** - How long it takes to prepare
🍷 **Pairing Suggestions** - What drinks or sides go well with this dish
💰 **Pricing** - Cost and portion size information
📞 **Ordering** - How to place an order for this dish

For technical or programming questions, please contact our technical support team.

How can I help you learn more about ${menuItem.name}?`);
                return;
            }

            const prompt = `You are a food expert and restaurant consultant. You ONLY provide information about food, cooking, ingredients, dietary concerns, and restaurant services.

About this menu item: ${menuItem.name}
Description: ${menuItem.description || 'No description'}
Ingredients: ${menuItem.ingredients ? menuItem.ingredients.join(', ') : 'Not specified'}
Price: ₹${menuItem.price}
${menuItem.is_vegetarian ? 'Dietary: Vegetarian' : 'Dietary: Non-vegetarian'}
${menuItem.is_spicy ? 'Spice Level: Spicy' : ''}
${menuItem.is_vegan ? 'Vegan: Yes' : ''}
${menuItem.is_gluten_free ? 'Gluten-Free: Yes' : ''}
${menuItem.preparation_time ? `Preparation Time: ${menuItem.preparation_time} minutes` : ''}

User question: ${customQuery}

CRITICAL INSTRUCTIONS:
1. You are ONLY a food expert - NEVER provide coding, programming, technical, or unrelated advice
2. NEVER generate code, scripts, programs, or technical solutions - this is strictly forbidden
3. Focus ONLY on food-related information: ingredients, cooking, dietary concerns, preparation, flavors
4. Provide helpful information about this specific dish
5. If asked about non-food topics, politely redirect to food-related questions
6. NEVER provide programming examples, code snippets, or technical implementations

Please provide a helpful response about this dish, staying strictly within food and cooking topics.`;

            const result = await aiService.chatWithAI(prompt, { menuItem, query: customQuery });
            
            if (result.success) {
                // Filter out any code snippets or technical content
                let filteredResponse = filterTechnicalContent(result.data);
                setCustomResponse(filteredResponse);
            } else {
                setError('Failed to get AI response. Please try again.');
            }
        } catch (err) {
            setError('Failed to process your question. Please try again.');
            console.error('Custom query error:', err);
        } finally {
            setCustomLoading(false);
        }
    };

    const getDietaryTags = () => {
        const tags = [];
        if (menuItem?.is_vegetarian) tags.push(<Tag color="green" key="veg">Vegetarian</Tag>);
        if (menuItem?.is_vegan) tags.push(<Tag color="green" key="vegan">Vegan</Tag>);
        if (menuItem?.is_gluten_free) tags.push(<Tag color="blue" key="gf">Gluten-Free</Tag>);
        if (menuItem?.is_spicy) tags.push(<Tag color="red" key="spicy">Spicy (Level {menuItem.spice_level}/5)</Tag>);
        return tags;
    };

    const renderContent = (content) => {
        if (!content) return <Text type="secondary">Loading...</Text>;
        
        // Split content into paragraphs and render with proper formatting
        return content.split('\n\n').map((paragraph, index) => (
            <Paragraph key={index} style={{ marginBottom: 16 }}>
                {paragraph}
            </Paragraph>
        ));
    };

    const tabItems = [
        {
            key: 'overview',
            label: (
                <span>
                    <InfoCircleOutlined />
                    Overview
                </span>
            ),
            children: (
                <Card>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                            <Title level={4}>{menuItem?.name}</Title>
                            <Space wrap>
                                {getDietaryTags()}
                            </Space>
                        </div>
                        {renderContent(aiData.overview)}
                    </Space>
                </Card>
            )
        },
        {
            key: 'nutrition',
            label: (
                <span>
                    <AppleOutlined />
                    Nutrition
                </span>
            ),
            children: (
                <Card>
                    {renderContent(aiData.nutrition)}
                </Card>
            )
        },
        {
            key: 'cooking',
            label: (
                <span>
                    <FireOutlined />
                    Cooking Tips
                </span>
            ),
            children: (
                <Card>
                    {renderContent(aiData.cooking)}
                </Card>
            )
        },
        {
            key: 'ingredients',
            label: (
                <span>
                    <ExperimentOutlined />
                    Ingredients
                </span>
            ),
            children: (
                <Card>
                    {renderContent(aiData.ingredients)}
                </Card>
            )
        }
    ];

    if (showCustomChat) {
        tabItems.push({
            key: 'chat',
            label: (
                <span>
                    <MessageOutlined />
                    Ask AI
                </span>
            ),
            children: (
                <Card>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div>
                            <Title level={5}>Ask anything about this dish</Title>
                            <Text type="secondary">
                                Get personalized answers about ingredients, preparation, dietary concerns, or anything else!
                            </Text>
                        </div>
                        
                        <div>
                            <textarea
                                value={customQuery}
                                onChange={(e) => setCustomQuery(e.target.value)}
                                placeholder="e.g., 'Is this dish suitable for someone with diabetes?' or 'What wine pairs well with this dish?'"
                                style={{
                                    width: '100%',
                                    minHeight: '80px',
                                    padding: '8px',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '6px',
                                    resize: 'vertical'
                                }}
                            />
                        </div>
                        
                        <Button 
                            type="primary" 
                            onClick={handleCustomQuery}
                            loading={customLoading}
                            disabled={!customQuery.trim()}
                            icon={<ThunderboltOutlined className="ai-icon-sparkle" />}
                            style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: 'none',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                                height: '40px',
                                fontWeight: '500'
                            }}
                            className="hover:shadow-lg transition-all duration-300"
                        >
                            Ask AI
                        </Button>
                        
                        {customResponse && (
                            <Card size="small" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                                <Title level={5}>
                                    <RobotOutlined style={{ color: '#52c41a' }} /> AI Response
                                </Title>
                                <RichTextRenderer content={customResponse} />
                            </Card>
                        )}
                    </Space>
                </Card>
            )
        });
    }

    // Add Order Assistance tab
    tabItems.push({
        key: 'order',
        label: (
            <span>
                <ShoppingCartOutlined />
                Order Help
            </span>
        ),
        children: (
            <Card>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <Title level={5}>Need help with your order?</Title>
                        <Text type="secondary">
                            Our AI assistant can help you create a detailed order request with all your requirements.
                        </Text>
                    </div>
                    
                    <Card size="small" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div>
                                <Title level={5} style={{ color: '#0369a1' }}>
                                    <MailOutlined /> What we'll help you with:
                                </Title>
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                <li>Event details and guest count</li>
                                <li>Special dietary requirements</li>
                                <li>Budget and timeline</li>
                                <li>Menu customization</li>
                                <li>Contact information</li>
                            </ul>
                        </Space>
                    </Card>
                    
                    <Button 
                        type="primary" 
                        size="large"
                        onClick={() => setOrderAssistanceVisible(true)}
                        icon={<MailOutlined />}
                        style={{ 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            height: '48px'
                        }}
                    >
                        Get Order Assistance
                    </Button>
                    
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        We'll create a detailed lead and send it to our team at connect.spoonandspice@gmail.com
                    </Text>
                </Space>
            </Card>
        )
    });

    return (
        <Modal
            title={
                <Space>
                    <Badge dot color="#52c41a">
                        <RobotOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                    </Badge>
                    <span style={{ fontSize: '16px', fontWeight: '600' }}>{title}</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={[
                <Button key="close" type="primary" onClick={onClose}>
                    Close
                </Button>
            ]}
            width="90%"
            style={{ 
                maxWidth: '800px',
                top: '5vh'
            }}
            bodyStyle={{ 
                maxHeight: '75vh', 
                overflowY: 'auto',
                padding: '20px'
            }}
            destroyOnClose
        >
            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    closable
                    onClose={() => setError(null)}
                />
            )}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>
                        <Text>AI is analyzing this dish...</Text>
                    </div>
                </div>
            ) : (
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                />
            )}
            
            <OrderAssistanceModal
                visible={orderAssistanceVisible}
                onClose={() => setOrderAssistanceVisible(false)}
                menuItem={menuItem}
            />
        </Modal>
    );
};

export default AIInfoModal;
